import Vue from 'vue';
import App from './App.vue';
import VueTippy, { TippyComponent } from 'vue-tippy';
import VModal from 'vue-js-modal';
import CustomMomentPluginBecauseVueMomentSucks from '@/moment';
import Vuelidate from 'vuelidate';
import VueCountryCode from 'vue-country-code';
import i18n from '@/i18n';
import router from '@/router';
import store from '@/store';
import '@/components/_global';
import '@/assets/tailwind.css';
import '@/sass/app.scss';
import '@/assets/accessibility.scss';
import Moment from '@/components/shared/Moment';
import Notifications from 'vue-notification';
import VueGtag from 'vue-gtag';
import VueGtm from '@gtm-support/vue2-gtm';
import VueApexCharts from 'vue-apexcharts';
import VueChatScroll from 'vue-chat-scroll';
import VueScrollTo from 'vue-scrollto';
import './registerServiceWorker';
import noSleep from 'nosleep.js';
import * as Sentry from '@sentry/vue';
import {
  registerPushServiceWorker,
  setAppBadge
} from '@/helpers/push-notifications';
import { setTabBadge } from '@/helpers/tab-badge';
import { applyBrandingToDocument } from '@/helpers/brand-theme';

Vue.component('Moment', Moment);
Vue.use(VueCountryCode);
Vue.use(Vuelidate);
Vue.use(CustomMomentPluginBecauseVueMomentSucks);
Vue.use(Notifications);
Vue.use(VueScrollTo);
Vue.use(VModal, {
  dynamicDefault: {
    draggable: false,
    resizable: false,
    focusTrap: true,
    clickToClose: true,
    classes: 'tappin-modal'
  }
});
Vue.use(VueTippy, { theme: 'tappin' });
Vue.component('tippy', TippyComponent);
Vue.use(VueChatScroll);

Vue.config.productionTip = false;

Vue.use(
  VueGtag,
  {
    config: { id: 'G-YLSK5DHX4C' }
  },
  router
);

Vue.use(VueGtm, {
  id: 'GTM-WK4TRWBB',
  enabled: true,
  debug: true,
  vueRouter: router
});

Vue.directive('focus', {
  inserted(el) {
    el.focus();
  }
});

Vue.use(VueApexCharts);
Vue.component('apexchart', VueApexCharts);

Sentry.init({
  Vue,
  dsn: 'https://7d3438fd9d60488f84036032e07ea31e@o4505489584226304.ingest.sentry.io/4505512401764352',
  integrations: [
    new Sentry.BrowserTracing({
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: [process.env.VUE_APP_API_BASE_URL],
      routingInstrumentation: Sentry.vueRouterInstrumentation(router)
    }),
    new Sentry.Replay()
  ],
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 0.1,
  tunnel: `${process.env.VUE_APP_API_BASE_URL}/sentry/tunnel`,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 0.1
});

new Vue({
  render: (h) => h(App),
  router,
  store,
  i18n
}).$mount('#app');

var nosleep = new noSleep();

// ── Push notifications (background / OS layer) ───────────────────────────
// Register the dedicated push-only service worker (no caching — see
// public/push-sw.js) and keep the tab-title + PWA app-icon badges in sync
// with the in-app unread count. In-page toast/sound/bell are handled by the
// `notifications` store module over the websocket; this adds the layer that
// fires when the tab is closed/backgrounded.
registerPushServiceWorker();

// When a notification is clicked, push-sw.js focuses this window and posts the
// click context — navigate in-app (SPA) instead of a full reload.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    const data = event.data;
    if (!data || data.type !== 'NOTIFICATION_CLICK') return;

    // Chat notifications open the chat page (optionally the specific
    // conversation when a userId is known); everything else opens its route.
    if (data.notificationType === 'chat') {
      const userId = data.meta && (data.meta.userId || data.meta.UserID);
      router.push(userId ? `/chat/${userId}` : '/chat').catch(() => {});
      return;
    }

    if (data.url && data.url !== '/') {
      router.push(data.url).catch(() => {});
    }
  });
}

function syncUnreadBadges() {
  const count = store.getters['Notifications/unreadCount'];
  setTabBadge(count);
  setAppBadge(count);
}

// ── Event branding → CSS custom properties ────────────────────────────
// Publishes the `--brand-*` layer on <html> whenever the event's
// branding changes (initial load, the post-login full-event payload, and
// the pusher `event-change` broadcast that fires when an admin saves in
// EMS). Everything downstream — `--color-primary`, `--ds-primary*`,
// `--t-primary*`, `--t-btn*` — derives from that layer in
// `sass/_variables.scss`, so no component needs to know about branding.
//
// Watching the getter rather than mutation names means the admin's
// "Button Colors" edit repaints the running platform live, without a
// reload, and switching events can never leave a stale colour behind.
store.watch(
  (_state, getters) => getters['CurrentEvent/getBranding'],
  (branding) => applyBrandingToDocument(branding),
  { immediate: true, deep: true }
);

// ── Robust push enrolment ──────────────────────────────────────────────
// For notifications to arrive when the app is CLOSED, a PushManager
// subscription must be saved on the backend — not just Notification
// permission. Permission is granted by the navbar and the event id arrives
// via middleware; either can happen first, and permission is often still
// 'default' when the event id first lands. So we attempt enrolment on every
// relevant signal and only stop once a subscription is actually saved.
// `PushNotifications/sync` is idempotent (subscribe reuses the existing
// subscription; the POST upserts by endpoint), so repeated attempts are safe.
// Tracked per user, not as a single flag: the server stores one subscription
// per browser under whoever enrolled it, so a different user signing in on the
// same browser must re-enrol or they would receive the previous user's pushes.
let pushEnrolledFor = null;
function ensurePushEnrolled() {
  const user = store.getters['Authentication/getUser'];
  const userId = (user && user.UserID) || null;
  // Wait for the profile; setUser is an Authentication/ mutation, so this
  // re-runs as soon as it lands.
  if (!userId || pushEnrolledFor === userId) return;
  if (!('Notification' in window) || Notification.permission !== 'granted')
    return;
  if (!store.getters['CurrentEvent/getId']) return;
  store.dispatch('PushNotifications/sync').then((ok) => {
    // retry later if it didn't actually subscribe
    pushEnrolledFor = ok ? userId : null;
  });
}

// 1) Attempt immediately (permission may already be granted from a prior visit).
ensurePushEnrolled();

// 2) Attempt when the event id becomes known OR the participant logs in
//    (the subscribe endpoint is authenticated — it must run only once a token
//    exists, which is why enrolment
store.subscribe((mutation) => {
  if (!mutation || !mutation.type) return;
  if (mutation.type.startsWith('Notifications/')) {
    syncUnreadBadges();
  }
  if (
    mutation.type.startsWith('CurrentEvent/') ||
    mutation.type.startsWith('Authentication/')
  ) {
    ensurePushEnrolled();
  }
});

// 3) Attempt the moment the user grants notification permission (e.g. via the
//    navbar prompt). The Permissions API fires a change event we react to.
if (navigator.permissions && navigator.permissions.query) {
  navigator.permissions
    .query({ name: 'notifications' })
    .then((status) => {
      status.onchange = () => {
        if (status.state === 'granted') ensurePushEnrolled();
      };
    })
    .catch(() => {
      /* Permissions API unavailable — the other triggers still cover it. */
    });
}

// An admin can edit a participant's answers while the app is already open. The profile (and with
// it every merge tag on the info pages) is otherwise only read at login / route entry, so re-read
// it when the participant comes back to the tab. Throttled; failures keep the current profile.
let lastProfileRefresh = 0;
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState !== 'visible') return;
  const now = Date.now();
  if (now - lastProfileRefresh < 30000) return;
  lastProfileRefresh = now;
  store.dispatch('Authentication/refreshUser');
});
